import React, { useState, useEffect, useCallback } from 'react';
import { evidenceService } from '../../../../services/api';

interface Evidence {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    type: string;
    description: string;
    contentHash: string;
    uploadedAt: string;
    uploadedBy: string;
}

interface Props {
    propertyId: string;
}

// Helper to calculate SHA-256 hash in browser
async function calculateSHA256(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export const EvidenceVault: React.FC<Props> = ({ propertyId }) => {
    const [evidence, setEvidence] = useState<Evidence[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);

    // Defensive: never map over a non-array (e.g. an envelope object slipped in).
    const evidenceItems = Array.isArray(evidence) ? evidence : [];

    const loadEvidence = useCallback(async () => {
        try {
            setLoading(true);
            const response = await evidenceService.listByProperty(propertyId);
            // The API now returns a pagination/entitlement envelope
            // ({ data: { items, total, upgrade } }) instead of a bare array.
            // Unwrap .items (falling back to legacy array shape) so callers never
            // receive an object — mapping over it threw "r.map is not a function"
            // and tripped the PropertyDetail error boundary.
            const payload = response?.data?.data;
            const list = Array.isArray(payload) ? payload : (payload?.items ?? []);
            setEvidence(Array.isArray(list) ? list : []);
            if (payload && !Array.isArray(payload) && payload.upgrade?.required) {
                setError("ACCESS_DENIED");
            }
        } catch (err: any) {
            console.error('[EvidenceVault] Failed to load:', err);
            if (err.response && err.response.status === 403) {
                setError("ACCESS_DENIED");
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        loadEvidence();
    }, [loadEvidence]);

    const handleFileUpload = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        setUploading(true);
        setError(null);

        try {
            for (const file of Array.from(files)) {
                // 1. Calculate SHA-256 hash
                const contentHash = await calculateSHA256(file);
                console.log('[EvidenceVault] Hash calculated:', contentHash.substring(0, 16) + '...');

                // 2. Get presigned URL
                const urlResponse = await evidenceService.getPresignedUrl({
                    fileName: file.name,
                    fileType: file.type,
                    propertyId
                });

                const { presignedUrl, s3Key } = urlResponse.data.data;

                // 3. Upload to S3
                await fetch(presignedUrl, {
                    method: 'PUT',
                    body: file,
                    headers: { 'Content-Type': file.type }
                });

                // 4. Confirm upload with hash
                await evidenceService.confirmUpload({
                    propertyId,
                    fileName: file.name,
                    s3Key,
                    fileSize: file.size,
                    fileType: file.type,
                    contentHash,
                    type: 'DOCUMENT',
                    description: ''
                });
            }

            loadEvidence();
        } catch (err: any) {
            console.error('[EvidenceVault] Upload failed:', err);
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (evidenceId: string) => {
        if (!window.confirm('Delete this evidence file?')) return;

        try {
            await evidenceService.delete(evidenceId, propertyId);
            loadEvidence();
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleDownload = async (item: Evidence) => {
        try {
            const response = await evidenceService.getDownloadUrl(propertyId, item.id);
            window.open(response.data.data.downloadUrl, '_blank');
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleVerify = async (item: Evidence) => {
        try {
            const response = await evidenceService.verify(propertyId, item.id, item.contentHash);
            const verified = response.data.data.verified;
            alert(verified ? '✅ Document integrity verified!' : '❌ Hash mismatch - document may have been modified');
        } catch (err: any) {
            setError(err.message);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="text-lg font-medium text-gray-900">
                        <i className="fas fa-archive mr-2 text-purple-600"></i>
                        Evidence Vault
                    </h3>
                    <p className="text-sm text-gray-500">Secure document storage with integrity verification</p>
                </div>
                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                    SHA-256 Protected
                </span>
            </div>

            {error && error === "ACCESS_DENIED" ? (
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg text-center mb-4">
                    <i className="fas fa-gem text-purple-500 text-2xl mb-2"></i>
                    <h4 className="text-purple-900 font-medium">Evidence Vault is a Pro Feature</h4>
                    <p className="text-purple-700 text-sm mt-1">Upgrade your plan to securely store and prove document integrity.</p>
                </div>
            ) : error && (
                <div className="bg-red-50 text-red-700 p-3 rounded mb-4 text-sm">
                    {error}
                </div>
            )}

            {/* Drop Zone */}
            <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4 ${dragOver ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
                    }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    handleFileUpload(e.dataTransfer.files);
                }}
                onClick={() => document.getElementById('evidence-file-input')?.click()}
            >
                <input
                    id="evidence-file-input"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files)}
                />
                {uploading ? (
                    <div className="text-purple-600">
                        <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                        <p>Uploading & hashing...</p>
                    </div>
                ) : (
                    <div className="text-gray-500">
                        <i className="fas fa-cloud-upload-alt text-3xl mb-2 text-purple-400"></i>
                        <p>Drop files here or click to upload</p>
                        <p className="text-xs mt-1">Photos, documents, correspondence</p>
                    </div>
                )}
            </div>

            {/* Evidence List */}
            {loading ? (
                <div className="text-center py-4 text-gray-500">Loading evidence...</div>
            ) : evidenceItems.length === 0 ? (
                <div className="text-center py-4 text-gray-400">
                    <i className="fas fa-folder-open text-3xl mb-2"></i>
                    <p>No evidence files yet</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {evidenceItems.map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                            <div className="flex items-center space-x-3">
                                <i className={`fas ${item.fileType?.includes('image') ? 'fa-image text-blue-500' :
                                    item.fileType?.includes('pdf') ? 'fa-file-pdf text-red-500' :
                                        'fa-file text-gray-500'
                                    } text-lg`}></i>
                                <div>
                                    <p className="font-medium text-gray-800 text-sm">{item.fileName}</p>
                                    <p className="text-xs text-gray-500">
                                        {formatFileSize(item.fileSize)} • {formatDate(item.uploadedAt)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handleVerify(item)}
                                    className="text-purple-600 hover:text-purple-800 text-sm"
                                    title="Verify Integrity"
                                >
                                    <i className="fas fa-shield-alt"></i>
                                </button>
                                <button
                                    onClick={() => handleDownload(item)}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                    title="Download"
                                >
                                    <i className="fas fa-download"></i>
                                </button>
                                <button
                                    onClick={() => handleDelete(item.id)}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                    title="Delete"
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-4 text-xs text-gray-400 text-center">
                <i className="fas fa-lock mr-1"></i>
                All files are cryptographically hashed for litigation provenance
            </div>
        </div>
    );
};
